import React, { useRef, useEffect } from "react";

/**
 * Reusable SearchableDropdown component
 * Supports searching, single/multi-select, and custom rendering
 * 
 * @param {Array} options - Array of options to display
 * @param {string|Array} selected - Selected value(s)
 * @param {Function} onSelect - Callback when option is selected
 * @param {string} placeholder - Placeholder text
 * @param {boolean} searchable - Enable search functionality
 * @param {boolean} multiselect - Enable multi-select mode
 * @param {string} searchQuery - Current search query
 * @param {Function} onSearchChange - Search query change handler
 * @param {boolean} isOpen - Dropdown open state
 * @param {Function} setIsOpen - Setter for dropdown state
 * @param {string} getOptionLabel - Function to get display label from option
 * @param {string} getOptionValue - Function to get value from option
 * @param {string} emptyMessage - Message when no options found
 * @param {boolean} clearable - Show clear button
 * @param {Function} onClear - Clear handler
 * @param {string} className - Additional CSS classes
 */
export default function SearchableDropdown({
    options = [],
    selected,
    onSelect,
    placeholder = "Select...",
    searchable = true,
    multiselect = false,
    searchQuery = "",
    onSearchChange,
    isOpen = false,
    setIsOpen,
    getOptionLabel = (option) => option?.name || option,
    getOptionValue = (option) => option?.id || option,
    emptyMessage = "Inga alternativ hittades",
    clearable = false,
    onClear,
    className = ""
}) {
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen, setIsOpen]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleSelectOption = (option) => {
        if (multiselect) {
            const value = getOptionValue(option);
            const selectedArray = Array.isArray(selected) ? selected : [];
            
            if (selectedArray.includes(value)) {
                onSelect(selectedArray.filter(v => v !== value));
            } else {
                onSelect([...selectedArray, value]);
            }
        } else {
            onSelect(option);
            setIsOpen(false);
        }
    };

    const isSelected = (option) => {
        const value = getOptionValue(option);
        if (multiselect) {
            return Array.isArray(selected) && selected.includes(value);
        }
        return getOptionValue(selected) === value;
    };

    const displayValue = () => {
        if (multiselect && Array.isArray(selected) && selected.length > 0) {
            return `${selected.length} selected`;
        }
        if (selected && !multiselect) {
            return getOptionLabel(selected);
        }
        return placeholder;
    };

    const filteredOptions = searchable && searchQuery
        ? options.filter(option => 
            getOptionLabel(option).toLowerCase().includes(searchQuery.toLowerCase())
          )
        : options;

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <div className="relative">
                {searchable ? (
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                        onFocus={() => setIsOpen(true)}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={handleToggle}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent text-left bg-white"
                    >
                        {displayValue()}
                    </button>
                )}
                
                {/* Dropdown Toggle Button */}
                <button
                    type="button"
                    onClick={handleToggle}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                    <svg 
                        className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Clear Button */}
                {clearable && selected && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear && onClear();
                        }}
                        className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Dropdown List */}
            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.length > 0 ? (
                        <>
                            {/* Clear option for single select */}
                            {!multiselect && clearable && (
                                <div 
                                    onClick={() => {
                                        onClear && onClear();
                                        setIsOpen(false);
                                    }}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-500 italic border-b border-gray-200"
                                >
                                    {placeholder}
                                </div>
                            )}
                            
                            {filteredOptions.map((option, index) => (
                                <div
                                    key={getOptionValue(option) || index}
                                    onClick={() => handleSelectOption(option)}
                                    className={`px-4 py-2 hover:bg-sage cursor-pointer transition-colors flex items-center justify-between ${
                                        isSelected(option) ? 'bg-sage font-semibold' : ''
                                    }`}
                                >
                                    <span>{getOptionLabel(option)}</span>
                                    {multiselect && isSelected(option) && (
                                        <svg className="w-5 h-5 text-forest" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            ))}
                        </>
                    ) : (
                        <div className="px-4 py-3 text-gray-500 text-center">
                            {emptyMessage}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
